import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EntraDefaultRoleScope,
  OrgUserInviteRole,
} from "../../../../autogen/bff-api";
import { useSelectableInviteRoles } from "./useSelectableInviteRoles";

interface Props {
  role: OrgUserInviteRole;
  scope: EntraDefaultRoleScope;
  holders: number;
  defaultDepartmentName: string;
  offersDefaultDepartment: boolean;
  isBusy: boolean;
  onSave: (role: OrgUserInviteRole, scope: EntraDefaultRoleScope) => Promise<void>;
  onClose: () => void;
}

export const EntraDefaultRoleModal = ({
  role: currentRole,
  scope: currentScope,
  holders,
  defaultDepartmentName,
  offersDefaultDepartment: choosesWhere,
  isBusy,
  onSave,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const inviteRoles = useSelectableInviteRoles();
  const roleName = useInviteRoleName();
  const where = useDefaultRoleWhere(defaultDepartmentName);

  const [role, setRole] = useState(currentRole);
  const [scope, setScope] = useState(currentScope);
  const isChanged = role !== currentRole || scope !== currentScope;

  return (
    <Modal isOpen onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{t("Default role for Entra ID users")}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap="4">
            <Flex columnGap="6" rowGap="3" flexWrap="wrap">
              <FormControl maxW="xs">
                <FormLabel fontSize="xs" color="smMuted" mb="1">
                  {t("Role")}
                </FormLabel>
                <Select
                  size="sm"
                  backgroundColor="smBackground"
                  value={role}
                  onChange={(e) => setRole(e.target.value as OrgUserInviteRole)}
                >
                  {inviteRoles.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {choosesWhere && (
                <FormControl maxW="xs">
                  <FormLabel fontSize="xs" color="smMuted" mb="1">
                    {t("Where it applies")}
                  </FormLabel>
                  <Select
                    size="sm"
                    backgroundColor="smBackground"
                    value={scope}
                    onChange={(e) => setScope(e.target.value as EntraDefaultRoleScope)}
                  >
                    <option value={EntraDefaultRoleScope.DefaultDepartment}>
                      {t("The default department ({{department}})", {
                        department: defaultDepartmentName,
                      })}
                    </option>
                    <option value={EntraDefaultRoleScope.Organization}>
                      {t("The whole organization")}
                    </option>
                  </Select>
                </FormControl>
              )}
            </Flex>

            <Text fontSize="sm">
              {choosesWhere
                ? t(
                    "Anyone who signs in without being in a group that gives access gets the role {{role}} in {{where}}.",
                    { role: roleName(role), where: where(scope) },
                  )
                : t("Anyone who signs in without being in a group that gives access gets the role {{role}}.", {
                    role: roleName(role),
                  })}
            </Text>

            {isChanged && holders > 0 && (
              <Text fontSize="sm" fontWeight="semibold">
                {t("Saving moves the {{count}} people who got access through the default role", {
                  count: holders,
                })}
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap="2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            size="sm"
            colorScheme="teal"
            isLoading={isBusy}
            isDisabled={!isChanged}
            onClick={() => void onSave(role, scope)}
          >
            {t("Save")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const useInviteRoleName = () => {
  const inviteRoles = useSelectableInviteRoles();
  return (role: OrgUserInviteRole) => inviteRoles.find((option) => option.value === role)?.label ?? role;
};

export const useDefaultRoleWhere = (defaultDepartmentName: string) => {
  const { t } = useTranslation();
  return (scope: EntraDefaultRoleScope) =>
    scope === EntraDefaultRoleScope.Organization ? t("the whole organization") : defaultDepartmentName;
};
